// pp-caja-3d.js — visor 3D interactivo de "tu producto dentro de la caja".
//
// Pedido de Padrino (1-sep-2026): "herramientitas visuales que el cliente
// pueda ver y darle vueltas". Lo usan el Optimizador de Fulfillment y el
// Buscador de Cajas.
//
// V2 (mismo día): la primera versión era CSS 3D (transform-style:
// preserve-3d) y en el Chrome de Padrino se veía como una MANCHA borrosa —
// los quirks de composición de preserve-3d anidado varían por navegador y
// GPU. Este motor dibuja la escena en CANVAS con proyección propia
// (rotación + perspectiva a mano): líneas nítidas garantizadas en
// cualquier navegador, y verificable por píxeles en headless. Cero
// librerías, igual que siempre.
//
// Lógica pura (orientación que cabe, escala y proyección) en PPCaja3D,
// probada en node.
(function (root) {
  'use strict';

  // Las 6 permutaciones de un cuboide.
  function permutaciones(d) {
    return [
      [d[0], d[1], d[2]], [d[0], d[2], d[1]],
      [d[1], d[0], d[2]], [d[1], d[2], d[0]],
      [d[2], d[0], d[1]], [d[2], d[1], d[0]]
    ];
  }

  // La orientación del producto que cabe en la caja Y se ve mejor: entre
  // las que caben, la de menor alto (los empacadores acuestan el producto).
  // null si no cabe. producto y caja: [l, w, h] cm (caja = interiores).
  function mejorOrientacion(producto, caja) {
    if (!producto || !caja) return null;
    var candidatas = [];
    var ps = permutaciones(producto);
    for (var i = 0; i < ps.length; i++) {
      var p = ps[i];
      if (p[0] <= caja[0] && p[1] <= caja[1] && p[2] <= caja[2]) candidatas.push(p);
    }
    if (!candidatas.length) return null;
    candidatas.sort(function (a, b) { return a[2] - b[2] || a[0] - b[0]; });
    return candidatas[0];
  }

  // Pixeles por cm para que la caja quepa en el viewport del visor.
  function escala(caja, viewportPx) {
    var max = Math.max(caja[0], caja[1], caja[2]);
    if (!(max > 0)) return 1;
    return (viewportPx || 220) / max;
  }

  // Proyecta un punto 3D [x, y, z] (y = arriba) a 2D con rotación
  // yaw (Y) → pitch (X) y perspectiva simple. Devuelve {x, y, z} donde z
  // es la profundidad ya rotada (para ordenar caras). Ángulos en radianes.
  function proyectar(p, yaw, pitch, foco) {
    var cy = Math.cos(yaw), sy = Math.sin(yaw);
    var cx = Math.cos(pitch), sx = Math.sin(pitch);
    // rotación Y
    var x1 = p[0] * cy + p[2] * sy;
    var z1 = -p[0] * sy + p[2] * cy;
    // rotación X
    var y2 = p[1] * cx - z1 * sx;
    var z2 = p[1] * sx + z1 * cx;
    var f = foco || 700;
    var k = f / (f + z2);
    return { x: x1 * k, y: -y2 * k, z: z2 };
  }

  // Los 8 vértices de un cuboide centrado en origen con base en y0.
  // dims [l, w, h] en px; y0 = piso (px hacia arriba desde el centro Y).
  function vertices(dims, y0) {
    var hx = dims[0] / 2, hz = dims[1] / 2, h = dims[2];
    var y = y0 || 0;
    return [
      [-hx, y, -hz], [hx, y, -hz], [hx, y, hz], [-hx, y, hz],
      [-hx, y + h, -hz], [hx, y + h, -hz], [hx, y + h, hz], [-hx, y + h, hz]
    ];
  }

  // Caras como índices de vértices (para pintar con painter's algorithm).
  var CARAS = [
    [0, 1, 2, 3], // piso
    [4, 5, 6, 7], // tapa
    [0, 1, 5, 4], // frente
    [2, 3, 7, 6], // atrás
    [1, 2, 6, 5], // derecha
    [3, 0, 4, 7]  // izquierda
  ];

  // Normal local de cada cara (mismo orden que CARAS).
  var NORMALES = [
    [0, -1, 0], // piso
    [0, 1, 0],  // tapa
    [0, 0, -1], // frente
    [0, 0, 1],  // atrás
    [1, 0, 0],  // derecha
    [-1, 0, 0]  // izquierda
  ];

  // Solo rota (sin perspectiva): para saber si una cara mira a la cámara.
  function rotar(p, yaw, pitch) {
    var cy = Math.cos(yaw), sy = Math.sin(yaw);
    var cx = Math.cos(pitch), sx = Math.sin(pitch);
    var x1 = p[0] * cy + p[2] * sy;
    var z1 = -p[0] * sy + p[2] * cy;
    return { x: x1, y: p[1] * cx - z1 * sx, z: p[1] * sx + z1 * cx };
  }

  // Aristas (12) como pares de índices.
  var ARISTAS = [
    [0, 1], [1, 2], [2, 3], [3, 0],
    [4, 5], [5, 6], [6, 7], [7, 4],
    [0, 4], [1, 5], [2, 6], [3, 7]
  ];

  // Radios del corte de rollo por conservación de área (puro, va aquí para
  // testearse en node): área de película = largo × espesor.
  function radiosDeRolloPuro(e) {
    var rc = e.radioNucleoCm || 3.81;
    var t = e.micras / 10000;
    var rReal = Math.sqrt(rc * rc + (e.metrosReales * 100) * t / Math.PI);
    var rProm = Math.sqrt(rc * rc + (e.metrosPrometidos * 100) * t / Math.PI);
    return { nucleo: rc, real: rReal, prometido: rProm };
  }

  root.PPCaja3D = {
    radiosDeRollo: radiosDeRolloPuro,
    permutaciones: permutaciones,
    mejorOrientacion: mejorOrientacion,
    escala: escala,
    proyectar: proyectar,
    rotar: rotar,
    vertices: vertices,
    NORMALES: NORMALES,
    CARAS: CARAS,
    ARISTAS: ARISTAS
  };
})(typeof window !== 'undefined' ? window : globalThis);

// ── DOM: dibujar y girar la escena en canvas (no corre en node) ───────────
(function () {
  'use strict';
  if (typeof document === 'undefined') return;
  var R = window.PPCaja3D;

  var NAVY = '#0E3A5C';
  // Un tono por pieza (frente / lados / tapa) para distinguirlas juntas
  var PALETA = [
    ['#C9A96E', '#B8955C', '#D8BB85'], // accent (el clásico)
    ['#7FA8C9', '#6B92B3', '#9FC0DB'], // azul suave
    ['#8FBF9F', '#79A98A', '#ACD3BA'], // verde suave
    ['#C98F8F', '#B37B7B', '#DBA9A9'], // terracota suave
    ['#B39DC9', '#9C87B3', '#CBB8DB'], // lila suave
    ['#B08D57', '#997747', '#C4A26B']  // madera (tarima)
  ];

  // Monta el visor. opciones: caja [l,w,h] cm interiores · producto [l,w,h]
  // cm (opcional) · etiqueta (textContent).
  function montar(contenedor, opciones) {
    contenedor.textContent = '';
    var caja = opciones.caja;
    var esc = R.escala(caja, 150);
    var W = 340, H = 260;
    var dpr = Math.min(window.devicePixelRatio || 1, 2);

    var marco = document.createElement('div');
    marco.className = 'pp-c3d';
    var canvas = document.createElement('canvas');
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    canvas.style.width = W + 'px';
    canvas.style.height = H + 'px';
    canvas.setAttribute('aria-label', 'Vista 3D del producto dentro de la caja — arrastra para girar');
    marco.appendChild(canvas);
    contenedor.appendChild(marco);

    if (opciones.etiqueta) {
      var pie = document.createElement('p');
      pie.className = 'pp-c3d__pie';
      pie.textContent = opciones.etiqueta;
      contenedor.appendChild(pie);
    }

    var ctx = canvas.getContext('2d');
    var cajaPx = [caja[0] * esc, caja[1] * esc, caja[2] * esc];
    var y0 = -cajaPx[2] / 2; // el piso de la caja, centrado verticalmente

    // Piezas a dibujar: o varias (modo "viajan juntos", con posición desde
    // la esquina interior de la caja) o una sola centrada en el piso.
    var piezas = [];
    if (opciones.piezas && opciones.piezas.length) {
      piezas = opciones.piezas.map(function (pz, idx) {
        var d = [pz.dims[0] * esc, pz.dims[1] * esc, pz.dims[2] * esc];
        return {
          dims: d,
          // esquina interior → coordenadas centradas del visor
          cx: pz.pos[0] * esc + d[0] / 2 - cajaPx[0] / 2,
          cz: pz.pos[2] * esc + d[1] / 2 - cajaPx[1] / 2,
          cy: y0 + pz.pos[1] * esc,
          color: (pz.color === undefined ? idx : pz.color),
          alfa: (pz.alfa === undefined ? 1 : pz.alfa)
        };
      });
    } else if (opciones.producto) {
      var ori = R.mejorOrientacion(opciones.producto, caja);
      if (ori) {
        var d1 = [ori[0] * esc, ori[1] * esc, ori[2] * esc];
        piezas = [{ dims: d1, cx: 0, cz: 0, cy: y0, color: 0, alfa: 1 }];
      }
    }

    function dibujar(yaw, pitch) {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, W, H);
      ctx.save();
      ctx.translate(W / 2, H / 2);

      var vCaja = R.vertices(cajaPx, y0).map(function (p) { return R.proyectar(p, yaw, pitch); });

      // Sombra de piso: elipse suave bajo la caja
      ctx.save();
      ctx.globalAlpha = 0.10;
      ctx.fillStyle = '#0B1F33';
      var pisoY = Math.max(vCaja[0].y, vCaja[1].y, vCaja[2].y, vCaja[3].y) + 12;
      ctx.beginPath();
      ctx.ellipse(0, pisoY, cajaPx[0] * 0.62, cajaPx[1] * 0.2, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      var conCaja = opciones.mostrarCaja !== false;
      // Aristas TRASERAS de la caja (las más profundas), punteadas
      var aristas = R.ARISTAS.map(function (a) {
        return { a: vCaja[a[0]], b: vCaja[a[1]], z: (vCaja[a[0]].z + vCaja[a[1]].z) / 2 };
      }).sort(function (p, q) { return q.z - p.z; });
      ctx.lineWidth = 2;
      ctx.lineJoin = 'round';
      if (conCaja) {
        aristas.slice(0, 6).forEach(function (ar) {
          ctx.strokeStyle = 'rgba(14, 58, 92, 0.35)';
          ctx.setLineDash([5, 4]);
          ctx.beginPath(); ctx.moveTo(ar.a.x, ar.a.y); ctx.lineTo(ar.b.x, ar.b.y); ctx.stroke();
        });
        ctx.setLineDash([]);
      }

      // Las piezas: TODAS las caras de TODAS en un solo orden por
      // profundidad (painter global) para que se oculten bien entre sí.
      if (piezas.length) {
        // Orden por PIEZA (lejos → cerca) + descarte de caras traseras:
        // para cuboides alineados sin traslape es un orden EXACTO — el
        // painter por caras encimaba la madera de la tarima a las cajas.
        var ordenadas = piezas.map(function (pz, pi) {
          var centro = R.rotar([pz.cx, pz.cy + pz.dims[2] / 2, pz.cz], yaw, pitch);
          return { pz: pz, pi: pi, z: centro.z };
        }).sort(function (p, q) { return q.z - p.z; });
        var todas = [];
        ordenadas.forEach(function (o) {
          var pz = o.pz;
          var vs = R.vertices(pz.dims, 0).map(function (p) {
            return R.proyectar([p[0] + pz.cx, p[1] + pz.cy, p[2] + pz.cz], yaw, pitch);
          });
          R.CARAS.forEach(function (c, i) {
            if (R.rotar(R.NORMALES[i], yaw, pitch).z >= 0) return; // cara trasera
            todas.push({ v: vs, c: c, i: i, pi: o.pi });
          });
        });
        todas.forEach(function (f) {
          var pz = piezas[f.pi];
          var pal = PALETA[pz.color % PALETA.length];
          var color = f.i === 1 ? pal[2] : (f.i >= 4 ? pal[1] : pal[0]);
          ctx.globalAlpha = pz.alfa;
          ctx.fillStyle = color;
          ctx.strokeStyle = 'rgba(11, 31, 51, ' + (0.4 * pz.alfa) + ')';
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(f.v[f.c[0]].x, f.v[f.c[0]].y);
          for (var k = 1; k < 4; k++) ctx.lineTo(f.v[f.c[k]].x, f.v[f.c[k]].y);
          ctx.closePath();
          ctx.fill();
          ctx.stroke();
          ctx.globalAlpha = 1;
        });
      }

      // Aristas DELANTERAS de la caja, sólidas y firmes — el "cristal"
      if (conCaja) {
        aristas.slice(6).forEach(function (ar) {
          ctx.strokeStyle = NAVY;
          ctx.lineWidth = 2.5;
          ctx.beginPath(); ctx.moveTo(ar.a.x, ar.a.y); ctx.lineTo(ar.b.x, ar.b.y); ctx.stroke();
        });
      }

      ctx.restore();
    }

    // Giro: arrastre con pointer events + giro suave hasta el primer toque.
    var yaw = -0.6, pitch = -0.35, auto = true;
    try {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) auto = false;
    } catch (_) { auto = false; }
    var arrastrando = false, px0 = 0, py0 = 0;
    marco.addEventListener('pointerdown', function (ev) {
      arrastrando = true; auto = false;
      px0 = ev.clientX; py0 = ev.clientY;
      try { marco.setPointerCapture(ev.pointerId); } catch (_) {}
    });
    marco.addEventListener('pointermove', function (ev) {
      if (!arrastrando) return;
      yaw -= (ev.clientX - px0) * 0.01;
      pitch -= (ev.clientY - py0) * 0.01;
      if (pitch > 0.15) pitch = 0.15;   // no meterse por debajo del piso
      if (pitch < -1.35) pitch = -1.35; // tope: vista casi cenital
      px0 = ev.clientX; py0 = ev.clientY;
      dibujar(yaw, pitch);
    });
    marco.addEventListener('pointerup', function () { arrastrando = false; });
    marco.addEventListener('pointercancel', function () { arrastrando = false; });
    (function girar() {
      if (!document.contains(canvas)) return;
      if (auto) { yaw += 0.004; dibujar(yaw, pitch); }
      requestAnimationFrame(girar);
    })();
    dibujar(yaw, pitch);

    return { actualizar: function (nuevas) { montar(contenedor, nuevas); } };
  }

  // ── Corte transversal del rollo de emplaye ────────────────────────────
  // No es 3D girable: es EL dibujo que hace brutal el veredicto — cuánto
  // de tu rollo es película real, cuánto te prometieron y cuánto es puro
  // cartón del núcleo. Radios por conservación de área: r = √(rc² + L·t/π).
  var radiosDeRollo = R.radiosDeRollo;

  function dibujarRollo(contenedor, e) {
    contenedor.textContent = '';
    var r = radiosDeRollo(e);
    var W = 360, H = 250;
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var canvas = document.createElement('canvas');
    canvas.width = W * dpr; canvas.height = H * dpr;
    canvas.style.width = W + 'px'; canvas.style.height = H + 'px';
    canvas.style.display = 'block';
    canvas.style.margin = '0 auto';
    canvas.style.maxWidth = '100%';
    canvas.setAttribute('aria-label', 'Corte transversal del rollo: película real contra la prometida');
    contenedor.appendChild(canvas);
    var ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    var rMax = Math.max(r.prometido, r.real);
    var esc = 88 / rMax;
    var cx = W / 2, cy = H / 2;

    // La película real: anillo dorado brillante (contraste contra el cartón)
    ctx.beginPath();
    ctx.arc(cx, cy, r.real * esc, 0, Math.PI * 2);
    ctx.arc(cx, cy, r.nucleo * esc, 0, Math.PI * 2, true);
    ctx.fillStyle = '#D9B36B';
    ctx.fill();
    ctx.strokeStyle = '#0E3A5C';
    ctx.lineWidth = 2.5;
    ctx.beginPath(); ctx.arc(cx, cy, r.real * esc, 0, Math.PI * 2); ctx.stroke();

    // Lo prometido: contorno punteado (pintado por ENCIMA del anillo, para
    // que se vea también cuando el rollo trae de más y queda adentro)
    ctx.strokeStyle = 'rgba(14, 58, 92, 0.55)';
    ctx.setLineDash([6, 5]);
    ctx.lineWidth = 2;
    ctx.beginPath(); ctx.arc(cx, cy, r.prometido * esc, 0, Math.PI * 2); ctx.stroke();
    ctx.setLineDash([]);

    // El núcleo es un TUBO de cartón: anillo con el centro HUECO
    // (QA de Padrino: "¿el centro no debería verse vacío?" — sí).
    var rHueco = r.nucleo * esc * 0.62;
    ctx.beginPath();
    ctx.arc(cx, cy, r.nucleo * esc, 0, Math.PI * 2);
    ctx.arc(cx, cy, rHueco, 0, Math.PI * 2, true);
    ctx.fillStyle = '#8B6A44'; ctx.fill();
    ctx.strokeStyle = 'rgba(11, 31, 51, 0.6)'; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.arc(cx, cy, r.nucleo * esc, 0, Math.PI * 2); ctx.stroke();
    // el hueco: blanco de la hoja, con su borde interior del tubo
    ctx.beginPath(); ctx.arc(cx, cy, rHueco, 0, Math.PI * 2);
    ctx.fillStyle = '#ffffff'; ctx.fill();
    ctx.strokeStyle = 'rgba(11, 31, 51, 0.45)'; ctx.lineWidth = 1.5; ctx.stroke();

    // Etiquetas en cuadrantes opuestos con guía — jamás encimadas y con el
    // VALOR junto al nombre. Real arriba-derecha, prometido abajo-izquierda.
    ctx.font = '600 12px "Inter Tight", -apple-system, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#5E6B7A';
    ctx.fillText('núcleo', cx, cy);

    // Etiquetas en DOS líneas ancladas a las esquinas del canvas — el
    // texto en una sola línea se recortaba en las orillas (QA con
    // captura de Padrino: 'lo prometido: 800 pies' salía como ': 800 pies').
    function guia(px, py, tx, ty, lineas, color, alinear) {
      ctx.strokeStyle = color;
      ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(tx, ty); ctx.stroke();
      ctx.fillStyle = color;
      ctx.textAlign = alinear;
      ctx.textBaseline = 'middle';
      var xTexto = tx + (alinear === 'left' ? 5 : -5);
      ctx.fillText(lineas[0], xTexto, ty - 8);
      if (lineas[1]) {
        ctx.font = '700 13px "Inter Tight", -apple-system, sans-serif';
        ctx.fillText(lineas[1], xTexto, ty + 8);
        ctx.font = '600 12px "Inter Tight", -apple-system, sans-serif';
      }
    }
    var d45 = Math.SQRT1_2;
    var rBanda = (r.nucleo + r.real) / 2 * esc;
    var etReal = e.etiquetaReal || ['tu película real', ''];
    var etProm = e.etiquetaProm || ['lo prometido', ''];
    if (typeof etReal === 'string') etReal = [etReal, ''];
    if (typeof etProm === 'string') etProm = [etProm, ''];
    guia(cx + rBanda * d45, cy - rBanda * d45, cx + rMax * esc + 16, cy - rMax * esc * 0.62, etReal, '#8a6d3f', 'left');
    guia(cx - r.prometido * esc * d45, cy + r.prometido * esc * d45, cx - rMax * esc - 16, cy + rMax * esc * 0.62, etProm, 'rgba(14, 58, 92, 0.85)', 'right');
    return canvas;
  }

  window.PPCaja3D.radiosDeRollo = radiosDeRollo;
  window.PPCaja3D.dibujarRollo = dibujarRollo;
  window.PPCaja3D.montar = montar;
})();
